<template>
  <v-row>
    <v-col cols="12">
      <base-card>
        <v-card-title>
          <v-btn
            dark
            color="secondary"
            class="ma-2"
            outlined
            @click="$router.back()"
          >
            <v-icon>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </v-card-title>
        <v-card-title class="pl-6">Course Item Question</v-card-title>
        <div class="pa-5">
          <v-form ref="form">
            <div class="d-flex mb-8 pb-2">
              <v-icon class="pr-2 html-icon">mdi-text</v-icon>
              <div class="w-full pl-1">
                <div class="mb-3 label grey--text">Content</div>

                <HtmlEditor
                  v-model="question.body_text"
                  :className="
                    htmlRequired ? 'html-body-error w-100 mt-2' : 'w-100 mt-2'
                  "
                ></HtmlEditor>
              </div>
            </div>
            <v-text-field
              label="Order"
              type="number"
              :rules="numberRules"
              v-model="question.order"
              prepend-icon="mdi-numeric"
              outlined
            />
            <v-text-field
              label="Score"
              type="number"
              :rules="numberRules"
              v-model="question.score"
              prepend-icon="mdi-numeric"
              outlined
            />
            <v-text-field
              label="Max Amount Try"
              type="number"
              v-model="question.max_amount_try"
              prepend-icon="mdi-numeric"
              outlined
            />
            <v-text-field
              label="Question Timeout Correct Answer"
              type="number"
              v-model="question.question_timeout_correct_answer"
              prepend-icon="mdi-numeric"
              outlined
            />
            <v-text-field
              label="Question Timeout Wrong Answer"
              type="number"
              v-model="question.question_timeout_wrong_answer"
              prepend-icon="mdi-numeric"
              outlined
            />
            <v-select
              label="Answer Type"
              :items="getAnswerTypes"
              item-text="name"
              item-value="id"
              prepend-icon="mdi-format-list-bulleted-square"
              outlined
              :rules="[requiredRule]"
              v-model="question.answer_type_id"
            ></v-select>
            <div class="d-flex mb-8 pb-2">
              <v-icon class="pr-2 html-icon">mdi-text</v-icon>
              <div class="w-full pl-1">
                <div class="mb-3 label grey--text">Help Text</div>

                <HtmlEditor
                  v-model="question.help_text"
                  className="w-100 mt-2"
                ></HtmlEditor>
              </div>
            </div>
            <div class="d-flex mb-8 pb-2">
              <v-icon class="pr-2 html-icon">mdi-text</v-icon>
              <div class="w-full pl-1">
                <div class="mb-3 label grey--text">Help Text 2</div>

                <HtmlEditor
                  v-model="question.help_text_two"
                  className="w-100 mt-2"
                ></HtmlEditor>
              </div>
            </div>
            <template v-if="question.answer_type_id === answerTypes.TEXT">
              <v-select
                label="Number Of Correct Answers"
                :items="numberOptions"
                item-text="value"
                item-value="value"
                prepend-icon="mdi-format-list-bulleted-square"
                outlined
                v-model="question.correct_answers_number"
                @change="updateCorrectFields()"
              ></v-select>
              <template v-for="(answer, i) in correctAnswers">
                <v-text-field
                  :label="`Correct Answer [${i + 1}]`"
                  outlined
                  :rules="[requiredRule]"
                  prepend-icon="mdi-text"
                  v-model="answer.body_text"
                  color="primary"
                ></v-text-field>
              </template>

              <v-select
                label="Number Of Wrong Answers"
                :items="numberOptions"
                item-text="value"
                item-value="value"
                prepend-icon="mdi-format-list-bulleted-square"
                outlined
                v-model="question.wrong_answers_number"
                @change="updateWrongFields()"
              ></v-select>
              <template v-for="(answer, i) in wrongAnswers">
                <v-text-field
                  :label="`Wrong Answer [${i + 1}]`"
                  outlined
                  :rules="[requiredRule]"
                  prepend-icon="mdi-text"
                  v-model="answer.body_text"
                  color="primary"
                ></v-text-field>
              </template>
            </template>

            <template v-if="question.answer_type_id === answerTypes.IMAGE">
              <image-upload
                class="pa-0 mb-7"
                label="Correct Answer"
                v-model="question.correct_answer_image.body_text"
                :rules="[requiredRule]"
                storageDir="img/answers"
                disk="answers"
              ></image-upload>

              <template v-for="(answer, i) in imageAnswers">
                <image-upload
                  class="pa-0 mb-7"
                  :label="`Answer [${i + 1}]`"
                  v-model="answer.body_text"
                  :rules="[requiredRule]"
                  storageDir="img/answers"
                  disk="answers"
                ></image-upload>
              </template>
            </template>

            <v-row class="mt-5" justify="end">
              <v-btn
                color="primary"
                @click="save"
                :loading="getCourseItemQuestionsLoading"
                :disabled="getCourseItemQuestionsLoading"
              >
                {{ isCreate ? "Create Question" : "Save changes" }}
              </v-btn>
            </v-row>
          </v-form>
        </div>
      </base-card>
    </v-col>
  </v-row>
</template>

<script>
import HtmlEditor from "@/components/HtmlEditor";
import ImageUpload from "@/components/ImageUpload.vue";
import { ANSWER_TYPES } from "@/shared/constants";
import { convertObjectToFormData } from "@/shared/utils";
import {
  dropRight,
  find,
  forEach,
  get,
  isEmpty,
  omit,
  size,
  trim,
} from "lodash";
import { mapActions, mapGetters } from "vuex";

export default {
  metaInfo: {
    title: "Course Item Question",
  },
  components: { HtmlEditor, ImageUpload },
  async mounted() {
    this.getAnswerTypes.length
      ? this.getAnswerTypes
      : await this.getAllAnswerTypes();

    if (this.questionId) {
      this.question = omit(
        find(this.getCourseItemQuestions, { id: +this.questionId }),
        ["id"]
      );
    }

    if (isEmpty(this.question.correct_answers)) {
      this.question.correct_answers.push({
        body_text: "",
      });
    }

    if (isEmpty(this.question.wrong_answers)) {
      this.question.wrong_answers.push({
        body_text: "",
      });
    }

    if (isEmpty(this.question.image_answers)) {
      for (let i = 1; i < 4; i++) {
        this.question.image_answers.push({
          body_text: null,
          file: null,
        });
      }
    }
  },
  data() {
    return {
      id: get(this.$route, "params.id"),
      itemId: get(this.$route, "params.itemId"),
      questionId: get(this.$route, "params.questionId"),
      answerTypes: ANSWER_TYPES,
      htmlRequired: false,
      question: {
        body_text: "",
        help_text: "",
        help_text_two: "",
        order: null,
        score: null,
        max_amount_try: null,
        question_timeout: null,
        question_timeout_correct_answer: null,
        question_timeout_wrong_answer: null,
        answer_type_id: ANSWER_TYPES.TEXT,
        correct_answers_number: 1,
        wrong_answers_number: 1,
        correct_answers: [],
        wrong_answers: [],
        correct_answer_image: {
          body_text: null,
        },
        image_answers: [],
      },
      requiredRule: (v) => !!v || "This is required.",
      numberRules: [
        (v) => !!trim(v) || "This is required.",
        (v) =>
          (!isNaN(parseFloat(v)) && v >= 0) ||
          "Number must be greater than or equal to 0.",
      ],
    };
  },
  methods: {
    ...mapActions([
      "showSnackbar",
      "setAnswerTypes",
      "setCourseParentItems",
      "createQuestion",
      "updateQuestion",
    ]),

    async getAllAnswerTypes() {
      return await this.setAnswerTypes();
    },

    updateCorrectFields() {
      if (
        this.question.correct_answers_number <
        size(this.question.correct_answers)
      ) {
        const removeSize =
          size(this.question.correct_answers) -
          this.question.correct_answers_number;

        this.question.correct_answers = dropRight(
          this.question.correct_answers,
          removeSize
        );
      } else {
        for (
          let i = size(this.question.correct_answers);
          i < this.question.correct_answers_number;
          i++
        ) {
          this.question.correct_answers.push({
            body_text: "",
          });
        }
      }
    },

    updateWrongFields() {
      if (
        this.question.wrong_answers_number < size(this.question.wrong_answers)
      ) {
        const removeSize =
          size(this.question.wrong_answers) -
          this.question.wrong_answers_number;

        this.question.wrong_answers = dropRight(
          this.question.wrong_answers,
          removeSize
        );
      } else {
        for (
          let i = size(this.question.wrong_answers);
          i < this.question.wrong_answers_number;
          i++
        ) {
          this.question.wrong_answers.push({
            body_text: "",
          });
        }
      }
    },

    async save() {
      this.htmlRequired = false;

      if (!this.$refs.form.validate() || !get(this.question, "body_text")) {
        if (!get(this.question, "body_text")) {
          this.htmlRequired = true;
        }

        this.$store.dispatch("showSnackbar", "One or more fields are invalid.");

        return;
      }

      let formData = convertObjectToFormData(this.question);

      formData.set("course_item_id", this.item.id);
      formData.set("max_amount_try", this.question.max_amount_try || 2);
      formData.set(
        "question_timeout_correct_answer",
        this.question.question_timeout_correct_answer || 0
      );
      formData.set(
        "question_timeout_wrong_answer",
        this.question.question_timeout_wrong_answer || 0
      );

      formData.set("help_text", this.question.help_text || "<p></p>");
      formData.set("help_text_two", this.question.help_text_two || "<p></p>");

      forEach(this.question.correct_answer_image, function (value, key) {
        formData.set(`correct_answer_image[${key}]`, value);
      });

      forEach(this.question.correct_answers, function (answer, index) {
        formData.set(`correct_answers[${index}][id]`, answer.id);
        formData.set(`correct_answers[${index}][body_text]`, answer.body_text);
      });

      forEach(this.question.wrong_answers, function (answer, index) {
        formData.set(`wrong_answers[${index}][id]`, answer.id);
        formData.set(`wrong_answers[${index}][body_text]`, answer.body_text);
      });

      forEach(this.question.image_answers, function (answer, index) {
        formData.set(`image_answers[${index}][id]`, get(answer, "id", null));
        formData.set(`image_answers[${index}][body_text]`, answer.body_text);
      });

      const response = this.isCreate
        ? await this.createQuestion(formData)
        : await this.updateQuestion({ id: this.questionId, formData });

      if (!isEmpty(response)) {
        this.$store.dispatch(
          "showSnackbar",
          `Question ${this.isCreate ? "created" : "updated"}.`
        );

        this.$router.push({
          path: `/app/course/${this.course.id}/course-item/${this.item.id}`,
        });
      }
    },
  },
  computed: {
    ...mapGetters([
      "getCourseLookup",
      "getAnswerTypes",
      "getCourseParentItems",
      "getCourseItems",
      "getCourseItemQuestions",
      "getCourseItemQuestionsLoading",
    ]),

    isCreate() {
      return _.get(this.$route, "meta.kind") === "create";
    },

    course() {
      return get(find(this.getCourseLookup, { course_id: +this.id }), "course");
    },

    item() {
      return find(this.getCourseItems, { id: +this.itemId });
    },

    numberOptions() {
      const selections = [];

      for (let i = 1; i < 10; i++) {
        selections.push({
          value: i,
        });
      }

      return selections;
    },

    correctAnswers() {
      return this.question.correct_answers;
    },

    wrongAnswers() {
      return this.question.wrong_answers;
    },

    imageAnswers() {
      return this.question.image_answers;
    },
  },
};
</script>
<style lang="scss" scoped>
.label {
  font-size: 16px;
  padding-left: 8px;
}
</style>
